// src/engine/SimulationEngine.js

function heuristic(nodeA, nodeB) {
  return Math.hypot(nodeA.x - nodeB.x, nodeA.y - nodeB.y);
}

export class SimulationEngine {
  constructor(mapData, config, type = 'traditional') {
    this.mapData = JSON.parse(JSON.stringify(mapData));
    this.config = config;
    this.type = type; // 'traditional' or 'ai'
    
    // Generate virtual slots for each station area
    (this.mapData.areas || []).forEach(area => {
      if (['storage', 'pickup', 'dropoff'].includes(area.type)) {
        const accessNodes = this.mapData.nodes.filter(n => n.connectedAreaIds && n.connectedAreaIds.includes(area.id));
        if (accessNodes.length === 0) return;
        
        const palletSize = 16;
        const pad = 4;
        const slotSize = palletSize + pad;
        const cols = Math.floor(area.w / slotSize);
        const rows = Math.floor(area.h / slotSize);
        
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            const slotX = area.x + pad + (c * slotSize) + palletSize/2;
            const slotY = area.y + pad + (r * slotSize) + palletSize/2;
            
            let closestNode = accessNodes[0];
            let minDist = Infinity;
            accessNodes.forEach(n => {
              const d = Math.hypot(n.x - slotX, n.y - slotY);
              if (d < minDist) { minDist = d; closestNode = n; }
            });
            
            this.mapData.nodes.push({
              id: `slot_${area.id}_${r}_${c}`,
              x: slotX,
              y: slotY,
              type: area.type,
              isVirtualSlot: true,
              accessNodeId: closestNode.id,
              connectedAreaId: area.id
            });
          }
        }
      }
    });
    
    const fleet = parseInt(config.fleetSize) || 5;
    
    const agvSpeedMs = parseFloat(config.agvSpeed) || 1.5;
    const agvSpeedMms = agvSpeedMs * 1000;
    const ppm = mapData.scaleCalibration?.pixelsPerMm || 0.05;
    const speedPxPerSec = agvSpeedMms * ppm;
    this.speedPxPerTick = speedPxPerSec / 60;

    this.agvs = Array.from({ length: fleet }).map((_, i) => {
      const startNode = mapData.nodes.length > 0 ? mapData.nodes[i % mapData.nodes.length] : null;
      return {
        id: `agv_${i}`,
        currentNodeId: startNode ? startNode.id : null,
        x: startNode ? startNode.x : 0,
        y: startNode ? startNode.y : 0,
        state: 'idle', // idle, moving_to_pickup, moving_to_storage
        payload: null,
        path: [],
        targetNodeId: null,
        speed: this.speedPxPerTick,
        battery: 100,
        waitTicks: 0
      };
    });

    this.orders = [];
    this.completedOrders = 0;
    
    this.metrics = {
      throughput: 0,
      totalWaitTime: 0,
      totalBlockTime: 0,
      ticks: 0,
      totalOrdersGenerated: 0
    };
    
    this.occupiedPickupNodes = new Set();
    this.reservedPickupNodes = new Set(); // For pallets in storage designated for outbound
    this.reservedNodes = new Set();
    this.occupiedNodes = new Set();
    this.palletInventory = new Map();
    this.pendingGenerations = [];

    // Generate an initial order so the simulation starts immediately
    this.generateOrder();
  }

  findPath(startId, endId) {
    if (!startId || !endId) return [];
    
    const startNode = this.mapData.nodes.find(n => n.id === startId);
    const endNode = this.mapData.nodes.find(n => n.id === endId);
    
    const realStartId = startNode && startNode.isVirtualSlot ? startNode.accessNodeId : startId;
    const realEndId = endNode && endNode.isVirtualSlot ? endNode.accessNodeId : endId;
    
    const nodes = this.mapData.nodes;
    const edges = this.mapData.edges;

    const realStartNode = nodes.find(n => n.id === realStartId);
    const realEndNode = nodes.find(n => n.id === realEndId);

    if (!realStartNode || !realEndNode) return [];

    const openSet = [realStartNode];
    const cameFrom = new Map();
    const gScore = new Map();
    nodes.forEach(n => gScore.set(n.id, Infinity));
    gScore.set(realStartId, 0);

    const fScore = new Map();
    nodes.forEach(n => fScore.set(n.id, Infinity));
    fScore.set(realStartId, heuristic(realStartNode, realEndNode));

    while (openSet.length > 0) {
      openSet.sort((a, b) => fScore.get(a.id) - fScore.get(b.id));
      const current = openSet.shift();

      if (current.id === realEndId) {
        const path = [current];
        let currId = current.id;
        while (cameFrom.has(currId)) {
          currId = cameFrom.get(currId);
          path.unshift(nodes.find(n => n.id === currId));
        }
        return path;
      }

      const neighbors = edges
        .filter(e => {
           if (e.dir === 'uni') return e.from === current.id;
           return e.from === current.id || e.to === current.id;
        })
        .map(e => {
          const neighborId = e.from === current.id ? e.to : e.from;
          return nodes.find(n => n.id === neighborId);
        })
        .filter(Boolean);

      for (const neighbor of neighbors) {
        const tentative_gScore = gScore.get(current.id) + heuristic(current, neighbor);
        
        if (tentative_gScore < gScore.get(neighbor.id)) {
          cameFrom.set(neighbor.id, current.id);
          gScore.set(neighbor.id, tentative_gScore);
          fScore.set(neighbor.id, tentative_gScore + heuristic(neighbor, endNode));
          
          if (!openSet.some(n => n.id === neighbor.id)) {
            openSet.push(neighbor);
          }
        }
      }
    }
    
    return [];
  }

  getDockingPoint(nodeId) {
    const node = this.mapData.nodes.find(n => n.id === nodeId);
    if (!node) return null;
    
    if (node.isVirtualSlot) {
       const accessNode = this.mapData.nodes.find(n => n.id === node.accessNodeId);
       let alignX = accessNode.x;
       let alignY = accessNode.y;
       
       const cor = (this.mapData.corridors || []).find(c => c.id === accessNode.corridorId);
       if (cor) {
          if (cor.w >= cor.h) alignX = node.x;
          else alignY = node.y;
       }
       
       const points = [];
       if (alignX !== accessNode.x || alignY !== accessNode.y) {
           points.push({ id: `align_${node.id}`, x: alignX, y: alignY, isDocking: true });
       }
       points.push({ id: `docking_${node.id}`, x: node.x, y: node.y, isDocking: true });
       return points;
    }
    
    let drawX = node.x;
    let drawY = node.y;
    let alignX = node.x;
    let alignY = node.y;
    
    if (node.connectedAreaId || (node.connectedAreaIds && node.connectedAreaIds.length > 0)) {
       const aId = node.connectedAreaId || node.connectedAreaIds[0];
       const area = this.mapData.areas.find(a => a.id === aId);
       if (area) {
         // Determine docking location slightly inside the area
         drawX = Math.max(area.x + 10, Math.min(node.x, area.x + area.w - 10));
         drawY = Math.max(area.y + 10, Math.min(node.y, area.y + area.h - 10));
         
         // Try to align to the lane properly
         const cor = (this.mapData.corridors || []).find(c => c.id === node.corridorId);
         if (cor) {
            const isHorizontal = cor.w >= cor.h;
            if (isHorizontal) {
               alignX = drawX;
            } else {
               alignY = drawY;
            }
         }
       }
    }
    
    if (drawX !== node.x || drawY !== node.y) {
       const points = [];
       // Only add alignment point if it's different from the node
       if (alignX !== node.x || alignY !== node.y) {
           points.push({ id: `align_${node.id}`, x: alignX, y: alignY, isDocking: true });
       }
       points.push({ id: `docking_${node.id}`, x: drawX, y: drawY, isDocking: true });
       return points;
    }
    return null;
  }

  getQueueStatus() {
    return {
      pickupQueue: this.occupiedPickupNodes.size,
      dropoffQueue: Array.from(this.reservedNodes).filter(id => {
        const n = this.mapData.nodes.find(node => node.id === id);
        if (!n) return false;
        const areaId = n.connectedAreaId || (n.connectedAreaIds && n.connectedAreaIds[0]);
        const area = this.mapData.areas.find(a => a.id === areaId);
        return area && area.type === 'dropoff';
      }).length
    };
  }

  generateOrder(specificFlow = null) {
    let pickupNode, storageNode;

    const validFlows = (this.mapData.flows || []).filter(f => 
      (f.sourceAreaId || (f.sourceAreaIds && f.sourceAreaIds.length > 0)) && 
      (f.targetAreaId || (f.targetAreaIds && f.targetAreaIds.length > 0))
    );
    
    if (specificFlow || validFlows.length > 0) {
      // Use configured flows
      const flow = specificFlow || validFlows[Math.floor(Math.random() * validFlows.length)];
      const flowConfig = (this.config.flowSettings || {})[flow.id] || {};
      const storageLogic = flowConfig.storageLogic || 'closest';
      const retrievalLogic = flowConfig.retrievalLogic || 'fifo';

      const sources = flow.sourceAreaIds || (flow.sourceAreaId ? [flow.sourceAreaId] : []);
      const targets = flow.targetAreaIds || (flow.targetAreaId ? [flow.targetAreaId] : []);

      const sourceNodesAll = this.mapData.nodes.filter(n => n.isVirtualSlot && (
         sources.includes(n.connectedAreaId) || 
         (n.connectedAreaIds && n.connectedAreaIds.some(id => sources.includes(id)))
      ));
      
      const sourceNodes = sourceNodesAll.filter(n => {
         const areaId = sources.find(id => id === n.connectedAreaId || (n.connectedAreaIds && n.connectedAreaIds.includes(id)));
         const area = this.mapData.areas.find(a => a.id === areaId);
         
         if (area && area.type === 'storage') {
            // Must already have a pallet, and not be reserved for another pickup
            return this.occupiedNodes.has(n.id) && !this.reservedPickupNodes.has(n.id);
         } else {
            // Default to Pickup: spawn new load, so slot must be empty
            return !this.occupiedPickupNodes.has(n.id);
         }
      });
      
      const targetNodes = this.mapData.nodes.filter(n => n.isVirtualSlot && (
         targets.includes(n.connectedAreaId) || 
         (n.connectedAreaIds && n.connectedAreaIds.some(id => targets.includes(id)))
      ));

      
      if (sourceNodes.length === 0) {
         if (specificFlow) this.pendingGenerations.push(specificFlow.id);
         return; // No pallets in storage, or no space at pickup. Will retry.
      }
      
      if (sourceNodes.length > 0 && targetNodes.length > 0) {
        // Apply Retrieval Logic for Storage -> Dropoff flows
        const firstSourceAreaId = sources.find(id => sourceNodes[0].connectedAreaId === id || (sourceNodes[0].connectedAreaIds && sourceNodes[0].connectedAreaIds.includes(id)));
        const firstSourceArea = this.mapData.areas.find(a => a.id === firstSourceAreaId);
        
        if (firstSourceArea && firstSourceArea.type === 'storage') {
           if (retrievalLogic === 'fifo') {
              // Oldest first
              sourceNodes.sort((a, b) => (this.palletInventory.get(a.id) || 0) - (this.palletInventory.get(b.id) || 0));
           } else if (retrievalLogic === 'lifo') {
              // Newest first
              sourceNodes.sort((a, b) => (this.palletInventory.get(b.id) || 0) - (this.palletInventory.get(a.id) || 0));
           } else {
              // Random
              sourceNodes.sort(() => Math.random() - 0.5);
           }
           pickupNode = sourceNodes[0];
        } else {
           pickupNode = sourceNodes[Math.floor(Math.random() * sourceNodes.length)];
        }
        
        let validTargets = targetNodes.filter(n => n.id !== pickupNode.id && !this.occupiedNodes.has(n.id) && !this.reservedNodes.has(n.id));
        if (validTargets.length === 0) {
           targetNodes.forEach(n => this.occupiedNodes.delete(n.id));
           validTargets = targetNodes.filter(n => n.id !== pickupNode.id && !this.reservedNodes.has(n.id));
        }
        
        if (this.type === 'traditional') {
           if (storageLogic === 'random') {
              const pool = validTargets.length > 0 ? validTargets : targetNodes;
              storageNode = pool[Math.floor(Math.random() * pool.length)];
           } else if (storageLogic === 'furthest') {
              validTargets.sort((a, b) => heuristic(pickupNode, b) - heuristic(pickupNode, a));
              storageNode = validTargets.length > 0 ? validTargets[0] : targetNodes[0]; 
           } else {
              // Closest spots first (fallback for 'closest' or any invalid obsolete values)
              validTargets.sort((a, b) => heuristic(pickupNode, a) - heuristic(pickupNode, b));
              storageNode = validTargets.length > 0 ? validTargets[0] : targetNodes[0];
           }
        } else {
           // AI Smart Placement: Minimize total distance (Pickup -> Storage -> Dropoff)
           let futureDropoffNodes = [];
           if (this.mapData.flows) {
              const storageAreaId = targetNodes.length > 0 ? (targetNodes[0].connectedAreaId || (targetNodes[0].connectedAreaIds && targetNodes[0].connectedAreaIds[0])) : null;
              const nextFlow = this.mapData.flows.find(f => f.sourceAreaId === storageAreaId || (f.sourceAreaIds && f.sourceAreaIds.includes(storageAreaId)));
              if (nextFlow) {
                  const targetAreaId = nextFlow.targetAreaId || (nextFlow.targetAreaIds && nextFlow.targetAreaIds[0]);
                  futureDropoffNodes = this.mapData.nodes.filter(n => n.connectedAreaId === targetAreaId || (n.connectedAreaIds && n.connectedAreaIds.includes(targetAreaId)));
              }
           }
           
           if (futureDropoffNodes.length > 0) {
              const centerX = futureDropoffNodes.reduce((sum, n) => sum + n.x, 0) / futureDropoffNodes.length;
              const centerY = futureDropoffNodes.reduce((sum, n) => sum + n.y, 0) / futureDropoffNodes.length;
              const dropoffCenter = { x: centerX, y: centerY };
              
              validTargets.sort((a, b) => {
                 const costA = heuristic(pickupNode, a) + heuristic(a, dropoffCenter);
                 const costB = heuristic(pickupNode, b) + heuristic(b, dropoffCenter);
                 return costA - costB;
              });
           } else {
              validTargets.sort((a, b) => heuristic(pickupNode, a) - heuristic(pickupNode, b));
           }
           storageNode = validTargets.length > 0 ? validTargets[0] : targetNodes[0]; 
        }
        
        if (pickupNode && storageNode) {
          this.metrics.totalOrdersGenerated++;
          
          const sourceAreaId = sources.find(id => id === pickupNode.connectedAreaId || (pickupNode.connectedAreaIds && pickupNode.connectedAreaIds.includes(id)));
          const sourceArea = this.mapData.areas.find(a => a.id === sourceAreaId);
          if (sourceArea && sourceArea.type === 'storage') {
             this.reservedPickupNodes.add(pickupNode.id);
          } else {
             this.occupiedPickupNodes.add(pickupNode.id);
          }
          this.reservedNodes.add(storageNode.id);
          this.orders.push({
            id: `order_${Date.now()}_${Math.random()}`,
            pickupNodeId: pickupNode.id,
            storageNodeId: storageNode.id,
            createdAt: this.metrics.ticks,
            status: 'pending'
          });
        }
      }
    } else {
      // Fallback to random pickup/storage
      const pickupsAll = this.mapData.nodes.filter(n => n.isVirtualSlot && n.type === 'pickup');
      const pickups = pickupsAll.filter(n => !this.occupiedPickupNodes.has(n.id));
      const storages = this.mapData.nodes.filter(n => n.isVirtualSlot && n.type === 'storage');
      
      if (pickups.length > 0 && storages.length > 0) {
        pickupNode = pickups[Math.floor(Math.random() * pickups.length)];
        
        let validTargets = storages.filter(n => n.id !== pickupNode.id && !this.occupiedNodes.has(n.id) && !this.reservedNodes.has(n.id));
        if (validTargets.length === 0) {
           storages.forEach(n => this.occupiedNodes.delete(n.id));
           validTargets = storages.filter(n => n.id !== pickupNode.id && !this.reservedNodes.has(n.id));
        }
        
        if (this.type === 'traditional') {
           if (this.config.storageLogic === 'random') {
              const pool = validTargets.length > 0 ? validTargets : storages;
              storageNode = pool[Math.floor(Math.random() * pool.length)];
           } else if (this.config.storageLogic === 'furthest') {
              validTargets.sort((a, b) => heuristic(pickupNode, b) - heuristic(pickupNode, a));
              storageNode = validTargets.length > 0 ? validTargets[0] : storages[0];
           } else {
              validTargets.sort((a, b) => heuristic(pickupNode, a) - heuristic(pickupNode, b));
              storageNode = validTargets.length > 0 ? validTargets[0] : storages[0];
           }
        } else {
           // AI Fallback smart placement
           let futureDropoffNodes = this.mapData.nodes.filter(n => n.isVirtualSlot && n.type === 'dropoff');
           if (futureDropoffNodes.length > 0) {
              const centerX = futureDropoffNodes.reduce((sum, n) => sum + n.x, 0) / futureDropoffNodes.length;
              const centerY = futureDropoffNodes.reduce((sum, n) => sum + n.y, 0) / futureDropoffNodes.length;
              const dropoffCenter = { x: centerX, y: centerY };
              validTargets.sort((a, b) => {
                 const costA = heuristic(pickupNode, a) + heuristic(a, dropoffCenter);
                 const costB = heuristic(pickupNode, b) + heuristic(b, dropoffCenter);
                 return costA - costB;
              });
           } else {
              validTargets.sort((a, b) => heuristic(pickupNode, a) - heuristic(pickupNode, b));
           }
           storageNode = validTargets.length > 0 ? validTargets[0] : storages[0]; 
        }
         if (pickupNode && storageNode) {
           this.metrics.totalOrdersGenerated++;
           this.occupiedPickupNodes.add(pickupNode.id);
           this.reservedNodes.add(storageNode.id);
           this.orders.push({
             id: `order_${Date.now()}_${Math.random()}`,
             pickupNodeId: pickupNode.id,
             storageNodeId: storageNode.id,
             createdAt: this.metrics.ticks,
             status: 'pending'
           });
         }
      }
    }
  }

  assignTasks() {
    const idleAgvs = this.agvs.filter(a => a.state === 'idle');
    const pendingOrders = this.orders.filter(o => o.status === 'pending');

    if (this.type === 'traditional') {
      for (let i = 0; i < Math.min(idleAgvs.length, pendingOrders.length); i++) {
        const agv = idleAgvs[i];
        const order = pendingOrders[i];
        
        order.status = 'assigned';
        agv.state = 'moving_to_pickup';
        agv.targetNodeId = order.pickupNodeId;
        agv.payload = order;
        agv.path = this.findPath(agv.currentNodeId, order.pickupNodeId);
        if (agv.isDocked) {
            const exitDock = this.getDockingPoint(agv.lastDockedNodeId);
            if (exitDock && exitDock.length > 0) agv.path.unshift(exitDock[0]);
            agv.isDocked = false;
        }
        const dock = this.getDockingPoint(order.pickupNodeId);
        if (dock) agv.path.push(...dock);
      }
    } else {
      while (idleAgvs.length > 0 && pendingOrders.length > 0) {
        let bestAgvIdx = -1;
        let bestOrderIdx = -1;
        let shortestDist = Infinity;

        for (let i = 0; i < idleAgvs.length; i++) {
          for (let j = 0; j < pendingOrders.length; j++) {
            const agv = idleAgvs[i];
            const order = pendingOrders[j];
            const pickupNode = this.mapData.nodes.find(n => n.id === order.pickupNodeId);
            if (pickupNode) {
               const dist = heuristic(agv, pickupNode);
               if (dist < shortestDist) {
                 shortestDist = dist;
                 bestAgvIdx = i;
                 bestOrderIdx = j;
               }
            }
          }
        }

        if (bestAgvIdx !== -1 && bestOrderIdx !== -1) {
           const agv = idleAgvs.splice(bestAgvIdx, 1)[0];
           const order = pendingOrders.splice(bestOrderIdx, 1)[0];

           order.status = 'assigned';
           agv.state = 'moving_to_pickup';
           agv.targetNodeId = order.pickupNodeId;
           agv.payload = order;
           agv.path = this.findPath(agv.currentNodeId, order.pickupNodeId);
           if (agv.isDocked) {
               const exitDock = this.getDockingPoint(agv.lastDockedNodeId);
               if (exitDock && exitDock.length > 0) agv.path.unshift(exitDock[0]);
               agv.isDocked = false;
           }
           const dock = this.getDockingPoint(order.pickupNodeId);
           if (dock) agv.path.push(...dock);
        } else {
           break;
        }
      }
      
      for (const agv of idleAgvs) {
        let randomPickup;
        const validFlows = (this.mapData.flows || []).filter(f => 
          (f.sourceAreaId || (f.sourceAreaIds && f.sourceAreaIds.length > 0)) && 
          (f.targetAreaId || (f.targetAreaIds && f.targetAreaIds.length > 0))
        );
        
        if (validFlows.length > 0) {
           const flow = validFlows[Math.floor(Math.random() * validFlows.length)];
           const srcIds = flow.sourceAreaIds || (flow.sourceAreaId ? [flow.sourceAreaId] : []);
           const sourceNodes = this.mapData.nodes.filter(n => srcIds.includes(n.connectedAreaId) || (n.connectedAreaIds && n.connectedAreaIds.some(id => srcIds.includes(id))));
           if (sourceNodes.length > 0) {
              randomPickup = sourceNodes[Math.floor(Math.random() * sourceNodes.length)];
           }
        } else {
           const pickups = this.mapData.nodes.filter(n => n.type === 'pickup');
           if (pickups.length > 0) {
              randomPickup = pickups[Math.floor(Math.random() * pickups.length)];
           }
        }

        if (randomPickup && Math.random() > 0.95) {
           agv.path = this.findPath(agv.currentNodeId, randomPickup.id);
           if (agv.isDocked) {
               const exitDock = this.getDockingPoint(agv.lastDockedNodeId);
               if (exitDock && exitDock.length > 0) agv.path.unshift(exitDock[0]);
               agv.isDocked = false;
           }
           const dock = this.getDockingPoint(randomPickup.id);
           if (dock) agv.path.push(...dock);
           agv.targetNodeId = randomPickup.id;
        }
      }
    }
  }

  moveAgvs() {
    this.agvs.forEach(agv => {
      if (agv.path && agv.path.length > 0) {
        const targetNode = agv.path[0];
        let isBlocked = false;
        
        if (this.type === 'traditional') {
           for (const other of this.agvs) {
             if (other.id !== agv.id && heuristic(agv, other) < 20) {
               isBlocked = true;
               this.metrics.totalBlockTime++;
               break;
             }
           }
        } else {
           for (const other of this.agvs) {
             if (other.id !== agv.id && heuristic(agv, other) < 30) {
               agv.speed = (this.speedPxPerTick || 2) * 0.25; // slow down to 25%
               break;
             } else {
               agv.speed = (this.speedPxPerTick || 2);
             }
           }
        }

        if (isBlocked) return;

        const dx = targetNode.x - agv.x;
        const dy = targetNode.y - agv.y;
        const dist = Math.hypot(dx, dy);

        if (dist <= agv.speed) {
          agv.x = targetNode.x;
          agv.y = targetNode.y;
          if (!targetNode.isDocking) {
             agv.currentNodeId = targetNode.id;
          }
          agv.path.shift();

          if (agv.path.length === 0) {
            if (targetNode.isDocking) {
                agv.isDocked = true;
                agv.lastDockedNodeId = agv.targetNodeId;
            }
            this.handleDestinationReached(agv);
          }
        } else {
          agv.x += (dx / dist) * agv.speed;
          agv.y += (dy / dist) * agv.speed;
        }
      } else {
         if (agv.targetNodeId && agv.currentNodeId === agv.targetNodeId) {
             this.handleDestinationReached(agv);
         }
      }
    });
  }

  handleDestinationReached(agv) {
    if (agv.state === 'moving_to_pickup') {
      if (this.occupiedPickupNodes.has(agv.payload.pickupNodeId)) {
         this.occupiedPickupNodes.delete(agv.payload.pickupNodeId);
      }
      if (this.reservedPickupNodes.has(agv.payload.pickupNodeId)) {
         this.reservedPickupNodes.delete(agv.payload.pickupNodeId);
         this.occupiedNodes.delete(agv.payload.pickupNodeId);
         this.palletInventory.delete(agv.payload.pickupNodeId);
      }
      
      agv.state = 'moving_to_storage';
      agv.targetNodeId = agv.payload.storageNodeId;
      agv.path = this.findPath(agv.currentNodeId, agv.targetNodeId);
      if (agv.isDocked) {
          const exitDock = this.getDockingPoint(agv.lastDockedNodeId);
          if (exitDock && exitDock.length > 0) agv.path.unshift(exitDock[0]);
          agv.isDocked = false;
      }
      const dock = this.getDockingPoint(agv.targetNodeId);
      if (dock) agv.path.push(...dock);
      
      const waitTime = this.metrics.ticks - agv.payload.createdAt;
      this.metrics.totalWaitTime += waitTime;
      
    } else if (agv.state === 'moving_to_storage') {
      this.completedOrders++;
      this.reservedNodes.delete(agv.payload.storageNodeId);
      
      // Check if target area is dropoff or storage
      const targetNode = this.mapData.nodes.find(n => n.id === agv.payload.storageNodeId);
      const targetAreaId = targetNode ? (targetNode.connectedAreaId || (targetNode.connectedAreaIds && targetNode.connectedAreaIds[0])) : null;
      const targetArea = this.mapData.areas.find(a => a.id === targetAreaId);
      
      if (targetArea && targetArea.type === 'dropoff') {
         // Load is consumed
      } else {
         this.occupiedNodes.add(agv.payload.storageNodeId);
         this.palletInventory.set(agv.payload.storageNodeId, this.metrics.ticks);
      }
      
      this.metrics.throughput = this.completedOrders;
      
      this.orders = this.orders.filter(o => o.id !== agv.payload.id);
      agv.payload = null;

      if (this.type === 'traditional') {
        agv.state = 'idle';
        const waypoints = this.mapData.nodes.filter(n => n.type === 'normal');
        if (waypoints.length > 0) {
           const standby = waypoints[0];
           agv.path = this.findPath(agv.currentNodeId, standby.id);
           if (agv.isDocked) {
               const exitDock = this.getDockingPoint(agv.lastDockedNodeId);
               if (exitDock && exitDock.length > 0) agv.path.unshift(exitDock[0]);
               agv.isDocked = false;
           }
        }
      } else {
        agv.state = 'idle'; 
      }
    }
  }

  tick() {
    this.metrics.ticks++;
    this.assignTasks();
    this.moveAgvs();
    
    // Process pending order generations that were queued due to lack of pallets
    if (this.pendingGenerations && this.pendingGenerations.length > 0) {
       const pending = [...this.pendingGenerations];
       this.pendingGenerations = [];
       pending.forEach(flowId => {
          const flow = this.mapData.flows.find(f => f.id === flowId);
          if (flow) this.generateOrder(flow);
       });
    }
  }

  getState() {
    return {
      agvs: [...this.agvs],
      orders: this.orders,
      metrics: { ...this.metrics },
      occupiedNodes: this.occupiedNodes,
      queueStatus: this.getQueueStatus(),
      virtualSlots: this.mapData.nodes.filter(n => n.isVirtualSlot)
    };
  }
}
